import {
    IEvent,
    ICustomProjectField,
    IEventSpaces,
    IImage,
    IEventWithWorkspaces,
    IWorkspace,
    TAvailabilityType,
    IProjectDate
} from '~/types/model.types';
import { TShortTimezone } from '~/utils/timezone.utils';
import { ICustomFieldValue } from '~/components/CustomFields/custom-field-schemas.types';
import { IMediaElementProps } from '@honeybook/hbui/dist/components/HBMediaElement/HBMediaElement';
import { serverImageToHBMediaElement } from '~/utils/image.utils';
import { observable, action } from 'mobx';

export class Project {
    _id: string;
    custom_project_fields: ICustomProjectField[];
    @observable custom_project_fields_v2?: ICustomFieldValue[];
    @observable event_name: string;
    @observable event_type: string;
    event_timezone: TShortTimezone;
    @observable event_date: string | null;
    @observable event_end_date: string | null;
    @observable event_time_start: string | null;
    @observable event_time_end: string | null;
    @observable event_location: string;
    @observable _event_image?: IImage; // This image is preserved only for the re-selecting image from angular.
    @observable event_image: IMediaElementProps;
    is_in_view_mode?: boolean;
    workspace_id: string;
    @observable workspaces: IWorkspace[] = [];
    @observable event_budget: number;
    @observable event_guests: number;
    @observable event_details: string;
    @observable spaces: IEventSpaces[];
    availability_type: TAvailabilityType;
    @observable project_dates?: IProjectDate[];
    @observable lead_source: string;
    @observable lead_source_open_text: string;
    @observable hide_conflict_pvl_action?: boolean;
    @observable is_client_portal_exposed?: boolean;
    @observable created_from_lead_form_id?: string;
    @observable created_from_contact_form_id?: string;

    constructor(project: IEvent | IEventWithWorkspaces) {
        this._id = project._id;
        this.custom_project_fields = project.custom_project_fields;
        this.custom_project_fields_v2 = project.custom_project_fields_v2;
        this.event_name = project.event_name;
        this.event_type = project.event_type;
        this.event_timezone = project.event_timezone;
        this.event_date = project.event_date;
        this.event_end_date = project.event_end_date;
        this.event_time_start = project.event_time_start;
        this.event_time_end = project.event_time_end;
        this.event_location = project.event_location;
        this.is_in_view_mode = project.is_in_view_mode;
        this.loadImage(project.event_image);
        this._event_image = project.event_image;
        this.event_budget = project.event_budget;
        this.event_guests = project.event_guests;
        this.event_details = project.event_details;
        this.spaces = project.spaces;
        this.availability_type = project.availability_type;
        this.workspace_id = project.workspace_id;
        this.project_dates = project.project_dates;
        this.lead_source = project.lead_source;
        this.lead_source_open_text = project.lead_source_open_text;
        this.is_client_portal_exposed = project.is_client_portal_exposed;
        this.created_from_lead_form_id = project.created_from_lead_form_id;
        this.created_from_contact_form_id =
            project.created_from_contact_form_id;

        const workspacesData = (project as IEventWithWorkspaces).workspaces;
        if (workspacesData?.length) {
            this.workspaces = workspacesData.map(workspace => {
                // Workspace for some reason holds project but it suppose to be the opposite so I need to cancel the infinite loop.
                const formattedWorkspace: IWorkspace = {
                    ...workspace,
                    event: {
                        ...workspace.event,
                        workspaces: null
                    } as IEventWithWorkspaces
                };

                return formattedWorkspace;
            });
        }
    }

    loadImage(image: IImage) {
        this.event_image =
            image &&
            serverImageToHBMediaElement({
                ...image,
                crop_ratio: 'Custom' // The old angular choose image modal always crop the image but doesn't apply the crop_ratio field. I don't want to change the original code so I'm adding this line.
            });
    }

    @action
    setCustomProjectFieldsV2(updatedCustomFieldValues: ICustomFieldValue[]) {
        this.custom_project_fields_v2 = updatedCustomFieldValues;
    }
}
